import React, { useCallback, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { Pressable, type PressableStateCallbackType, View, type ViewStyle } from 'react-native'

import { createColumnHelper, type Row } from '@tanstack/react-table'
import styled, { useTheme } from 'styled-components/native'

import { convertUTCToISO8601format } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/convertUTCToISO8601format'
import { type Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { type Outcome } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import { type ProviderProgramTaxonomy } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { type OutcomeResponseDetailsViewData } from '@lyrahealth-inc/ui-core/lib/molecules/outcomeResponseDetails/OutcomeResponseDetails'
import { ButtonSize, ButtonType } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/baseButton/BaseButton'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import {
  DropdownButton,
  type DropdownMenuItem,
} from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dropdownButton/DropdownButton'
import {
  TypeAheadCustomOptionsConfig,
  ValueFetchMode,
} from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/typeAhead/TypeAheadCustomOptionsConfig'
import { AlertLoudIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AlertLoudIcon'
import { AssessmentIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AssessmentIcon'
import { CalendarEmptyIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CalendarEmptyIcon'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { ProgressIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ProgressIcon'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Tooltip, TooltipMaxWidth } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { DataTable } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/dataTable/DataTable'
import {
  type HoverActionItem,
  HoverActions,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/hoverActions/HoverActions'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { getAssessmentAccessibilityLabel, getAssessmentSubmissionDateTime } from './assessmentAttempt'
import { TruncatedText } from './CurrentMeasuresTable.TruncatedText'
import {
  EMPTY_VALUE,
  getAssessmentAnswers,
  getMeasureRows,
  getSelectableOutcomeDates,
  hasAssessmentAnswers,
  type MeasureRow,
} from './getMeasureRows'
import { parseOutcomeResponseDate } from './outcomeDate'

/**
 * Renders the outcome measures card.
 *
 * "Which measures, with which scores" lives entirely in `getMeasureRows`; this file is columns and
 * chrome only. Keep it that way — measure names, score ceilings and supplemental groupings belong
 * in `measureCatalog.ts`.
 */

export type ViewAnswersRequest = Omit<OutcomeResponseDetailsViewData, 'title'> & { title: string }

export type ViewSelfReflectionRequest = {
  dateSubmitted?: string
  text: string
}

export type CurrentMeasuresTableProps = {
  /** Renders provider-added rows and indicates when they own the card's visible shared headers. */
  renderAddedAssessments?: (options: { showSharedColumnHeaders: boolean }) => React.ReactNode
  /** Scoped to the selected episode and recipient — drives pending rows. */
  assignments?: Assignment[]
  /** Every assignment for the client; resolves self reflections by outcome assignment id. */
  selfReflectionAssignments?: Assignment[]
  clientName?: string
  /** True only while an initial outcomes or assignments request is in flight. */
  isLoading?: boolean
  /** Browser-local calendar date selected by the shared date control; undefined means latest. */
  selectedDateKey?: string
  onSelectedDateKeyChange: (dateKey: string | undefined) => void
  isProviderSupervisor?: boolean
  outcomes?: Outcome[]
  programTaxonomy?: Partial<ProviderProgramTaxonomy> | null
  providerId?: string
  onViewAnswers?: (request: ViewAnswersRequest) => void
  onViewSelfReflection?: (request: ViewSelfReflectionRequest) => void
  onGoToChart?: (chartKey: string) => void
  onViewPendingAssessment?: () => void
  onAddAssessment?: () => void
}

const LAST_KNOWN_MEASURES = 'last-known-measures'

const MESSAGES = defineMessages({
  currentMeasures: {
    defaultMessage: 'Outcome measures',
    description: 'Title of the card listing the client’s outcome assessment measures',
  },
  selectedDate: {
    defaultMessage: 'Selected date',
    description: 'Title of the card when viewing assessment measures from a selected date',
  },
  selectDate: {
    defaultMessage: 'Select date',
    description: 'Button to choose the date the current measures are shown for',
  },
  selectDateAccessibilityLabel: {
    defaultMessage: 'Select assessment date. Current selection: {selection}',
    description: 'Accessibility label for the control that filters assessments by date',
  },
  dateOptionsAccessibilityLabel: {
    defaultMessage: 'Assessment dates',
    description: 'Accessibility label for the list of dates that can filter assessments',
  },
  lastKnownMeasures: {
    defaultMessage: 'All outcome measures',
    description: 'Option to show the latest available result for each assessment measure',
  },
  currentMeasuresTable: {
    defaultMessage: '{title} assessment results',
    description: 'Accessibility label for the table of assessment results',
  },
  notAvailable: {
    defaultMessage: 'Not available',
    description: 'Accessibility label for an assessment value that is not available',
  },
  coreAssessmentsHeader: {
    defaultMessage: 'Core assessments',
    description: 'Column header for the list of core assessments',
  },
  coreAssessmentsTooltip: {
    defaultMessage:
      'Lyra’s core assessment package, automatically sent to every client. Some measures are paused automatically based on baseline results.',
    description: 'Tooltip explaining core assessments',
  },
  aboutCoreAssessments: {
    defaultMessage: 'About core assessments',
    description: 'Accessibility label for the core assessments information button',
  },
  addAssessment: {
    defaultMessage: '+ Add assessment',
    description: 'Button at the bottom of the assessments table that opens the add assessment modal',
  },
  addAssessmentAccessibilityLabel: {
    defaultMessage: 'Add assessment',
    description: 'Accessibility label for the button that opens the add assessment modal',
  },
  scoreHeader: { defaultMessage: 'Score', description: 'Column header for the latest assessment score' },
  descriptionHeader: {
    defaultMessage: 'Description',
    description: 'Column header for the assessment score severity description',
  },
  frequencyHeader: {
    defaultMessage: 'Frequency',
    description: 'Column header for how often an automated assessment is sent',
  },
  frequencyTooltip: {
    defaultMessage: 'Sent 72 hours in advance, per the selected frequency.',
    description: 'Tooltip explaining when a scheduled assessment is sent to the client',
  },
  aboutFrequency: {
    defaultMessage: 'About assessment frequency',
    description: 'Accessibility label for the frequency information button',
  },
  everySession: {
    defaultMessage: 'Every session',
    description: 'Frequency shown for an automated assessment that is sent ahead of every session',
  },
  screenerOnly: {
    defaultMessage: 'Screener only',
    description: 'Frequency shown when an assessment measure is only included in the initial screener',
  },
  screenerOnlyTooltip: {
    defaultMessage: 'Stopped automatically based on the client’s screener score.',
    description: 'Tooltip explaining why an assessment measure is no longer sent automatically',
  },
  aboutScreenerOnly: {
    defaultMessage: 'About screener-only status for {assessment}',
    description: 'Accessibility label for information about a screener-only assessment measure',
  },
  lastTakenHeader: {
    defaultMessage: 'Last submitted',
    description: 'Column header for the date the assessment was last submitted',
  },
  overallDescription: {
    defaultMessage: 'Overall - {description}',
    description: 'Summary line at the top of an assessment’s additional details tooltip',
  },
  additionalDetailsTrigger: {
    defaultMessage: '{description}. Additional assessment details for {assessment}',
    description: 'Accessibility label for an assessment description with additional details',
  },
  criticalAnswerTrigger: {
    defaultMessage: '{description}. Critical answer alert for {assessment}',
    description: 'Accessibility label for an assessment description with a critical answer',
  },
  viewAnswers: { defaultMessage: 'View answers', description: 'Row action opening a client’s assessment answers' },
  viewAnswersAccessibilityLabel: {
    defaultMessage: 'View answers for {assessment}',
    description: 'Accessibility label for the view answers row action',
  },
  showFullEntry: {
    defaultMessage: 'Show full entry',
    description: 'Row action opening a member’s complete self-reflection entry',
  },
  showFullEntryAccessibilityLabel: {
    defaultMessage: 'Show full entry for {assessment}',
    description: 'Accessibility label for the show full self-reflection entry row action',
  },
  goToChart: { defaultMessage: 'Go to chart', description: 'Row action scrolling to an assessment’s history chart' },
  goToChartAccessibilityLabel: {
    defaultMessage: 'Go to the history chart for {assessment}',
    description: 'Accessibility label for the go to chart row action',
  },
})

const COLUMN_WIDTH = {
  assessment: { preferred: 478, min: 80 },
  score: { preferred: 120, min: 60 },
  description: { preferred: 180, min: 60 },
  frequency: { preferred: 220, min: 100 },
  lastTakenDate: { preferred: 140, min: 80 },
} as const

const COLUMN_CONTENT_INSET = 16

type OutcomeDateOption = DropdownMenuItem & {
  accessibilityLabel: string
  triggerText?: string
  date?: Date
}

const CardContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  borderWidth: '1px',
  borderColor: colors.borderDefault,
  borderRadius: '16px',
  backgroundColor: colors.backgroundPrimary,
  overflow: 'hidden',
}))

const CardHeaderContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingHorizontal: spacing['16px'],
  paddingVertical: spacing['12px'],
  // Above the hover row-actions overlay (zIndex 2) so the open date dropdown paints over it.
  zIndex: 3,
}))

const AddAssessmentRow = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  height: 42,
  justifyContent: 'center',
  alignItems: 'flex-start',
  paddingHorizontal: spacing['16px'],
  backgroundColor: colors.backgroundPrimary,
}))

const SelectDateDropdown = styled(DropdownButton)<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundHighlightPrimary,
  borderColor: theme.colors.backgroundTransparent,
  borderWidth: 0,
  borderRadius: 100,
  height: 32,
  minWidth: 0,
  paddingHorizontal: theme.spacing['12px'],
}))

const HeaderCellContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flex: 1,
  minWidth: 0,
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
  overflow: 'hidden',
}))

const InlineCellContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flex: 1,
  minWidth: 0,
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['4px'],
  overflow: 'hidden',
}))

const AdditionalDetailsTooltipContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['12px'],
}))

const AdditionalDetailsTooltipItem = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['4px'],
}))

const AdditionalDetailsIconContainer = styled.View({
  flexShrink: 0,
})

const CurrentMeasureHoverActions = styled(HoverActions)<{ isVisible: boolean }>(({ isVisible }) => ({
  opacity: isVisible ? 1 : 0,
  pointerEvents: isVisible ? 'auto' : 'none',
}))

const AdditionalDetailsTooltip: React.FC<{
  rowId: string
  description?: string
  details: NonNullable<MeasureRow['supplementalTooltip']>['details']
}> = ({ rowId, description, details }) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const textColor = colors.components?.tooltip?.text ?? colors.textInverse

  return (
    <AdditionalDetailsTooltipContainer>
      {description && (
        <View accessible={false} aria-hidden importantForAccessibility='no-hide-descendants'>
          <BodyText
            text={formatMessage(MESSAGES.overallDescription, { description })}
            size={BodyTextSize.SMALL}
            color={textColor}
          />
        </View>
      )}
      {details.map(({ id, label, value }) => (
        <AdditionalDetailsTooltipItem key={id} testID={tID(`CurrentMeasuresTable-${rowId}-descriptionDetail-${id}`)}>
          <BodyText text={label} size={BodyTextSize.SMALL} color={textColor} medium wrap />
          <BodyText text={value} size={BodyTextSize.SMALL} color={textColor} wrap />
        </AdditionalDetailsTooltipItem>
      ))}
    </AdditionalDetailsTooltipContainer>
  )
}

const columnHelper = createColumnHelper<MeasureRow>()
const getMeasureRowId = (row: MeasureRow) => row.testId

const getColumnLayout = (column: keyof typeof COLUMN_WIDTH, flex = 0) => ({
  size: COLUMN_WIDTH[column].preferred,
  minSize: COLUMN_WIDTH[column].min,
  meta: {
    flex,
    inlineComponent: true,
    inlineHeaderComponent: true,
    shrinkToFit: true,
    ...(flex ? {} : { contentPaddingLeft: COLUMN_CONTENT_INSET }),
  },
})

export const CurrentMeasuresTable: React.FC<CurrentMeasuresTableProps> = ({
  renderAddedAssessments,
  assignments,
  selfReflectionAssignments,
  clientName,
  isLoading = false,
  selectedDateKey,
  onSelectedDateKeyChange,
  isProviderSupervisor,
  outcomes,
  programTaxonomy,
  providerId,
  onViewAnswers,
  onViewSelfReflection,
  onGoToChart,
  onViewPendingAssessment,
  onAddAssessment,
}) => {
  const intl = useIntl()
  const { formatDate, formatMessage } = intl
  const { colors } = useTheme()
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)
  const [focusedActionRowId, setFocusedActionRowId] = useState<string | null>(null)
  const [tableWidth, setTableWidth] = useState(0)

  // Each option owns every exact submission that falls on the same displayed calendar date.
  const dateOptions = useMemo<OutcomeDateOption[]>(() => {
    return [
      {
        id: LAST_KNOWN_MEASURES,
        text: formatMessage(MESSAGES.lastKnownMeasures),
        accessibilityLabel: formatMessage(MESSAGES.lastKnownMeasures),
        testId: 'CurrentMeasuresTable-lastKnownMeasures',
        selectHandler: () => onSelectedDateKeyChange(undefined),
      },
      ...getSelectableOutcomeDates(outcomes).map(({ key, date }, index) => {
        const shortDate = formatDate(date, { month: 'numeric', day: 'numeric', year: 'numeric' })
        const longDate = formatDate(date, { month: 'long', day: 'numeric', year: 'numeric' })

        return {
          id: key,
          text: shortDate,
          triggerText: shortDate,
          accessibilityLabel: longDate,
          testId: `CurrentMeasuresTable-dateOption-${index}`,
          selectHandler: () => onSelectedDateKeyChange(key),
          date,
        }
      }),
    ]
  }, [formatDate, formatMessage, onSelectedDateKeyChange, outcomes])

  const dateOptionsConfig = useMemo(
    () =>
      new TypeAheadCustomOptionsConfig<OutcomeDateOption>(
        dateOptions,
        undefined,
        undefined,
        'text',
        undefined,
        ValueFetchMode.ID,
      ),
    [dateOptions],
  )

  const selectedDateOption = dateOptions.find(({ id }) => id === selectedDateKey) ?? dateOptions[0]
  const hasSelectedDate = selectedDateKey !== undefined && selectedDateOption?.date !== undefined
  const tableTitle = formatMessage(hasSelectedDate ? MESSAGES.selectedDate : MESSAGES.currentMeasures)

  const rows = useMemo(
    () =>
      getMeasureRows({
        selectedDateKey,
        outcomes,
        assignments,
        selfReflectionAssignments,
        context: { programTaxonomy, providerId, isSupervisor: isProviderSupervisor },
        intl,
      }),
    [
      assignments,
      selfReflectionAssignments,
      intl,
      isProviderSupervisor,
      outcomes,
      programTaxonomy,
      providerId,
      selectedDateKey,
    ],
  )

  const shouldShowCoreAssessments = isLoading || selectedDateKey === undefined || rows.length > 0

  const openRowDetails = useCallback(
    (measureRow: MeasureRow) => {
      const {
        chartKey,
        isPending,
        lastTakenDate,
        measureDisplayName,
        outcome,
        rawScore,
        responseDetailsKey,
        selfReflectionText,
      } = measureRow

      if (isPending) {
        onViewPendingAssessment?.()
        return
      }
      if (selfReflectionText && onViewSelfReflection) {
        onViewSelfReflection({ dateSubmitted: lastTakenDate, text: selfReflectionText })
        return
      }
      if (!responseDetailsKey || !hasAssessmentAnswers(measureRow)) return

      const formatModalDate = (date?: string) =>
        date ? formatDate(convertUTCToISO8601format(date), { month: '2-digit', day: '2-digit', year: 'numeric' }) : ''
      // RCADS has verified instructions; chart-backed measures keep their existing copy.
      const shouldHideAnswerDescription = !chartKey && responseDetailsKey !== 'RCADS-TOTAL'

      onViewAnswers?.({
        clientName: clientName ?? '',
        title: measureDisplayName,
        assessmentType: chartKey ?? responseDetailsKey,
        score: rawScore,
        hideFinalScore: measureRow.hideFinalScore,
        answerDescription: shouldHideAnswerDescription ? null : undefined,
        dateStarted: formatModalDate(outcome?.date_created),
        dateSubmitted: formatModalDate(outcome?.response_date),
        answers: getAssessmentAnswers(measureRow),
      })
    },
    [clientName, formatDate, onViewAnswers, onViewPendingAssessment, onViewSelfReflection],
  )

  const handleRowPress = useCallback((row: Row<MeasureRow>) => openRowDetails(row.original), [openRowDetails])

  const canOpenRowDetails = Boolean(onViewAnswers || onViewSelfReflection || onViewPendingAssessment)

  const renderRowActions = useMemo(() => {
    if (!onViewAnswers && !onViewSelfReflection && !onGoToChart && !onViewPendingAssessment) return undefined

    return (row: Row<MeasureRow>, isHovered: boolean) => {
      const { original } = row
      const { chartKey, isPending, outcome, selfReflectionText, testId } = original
      const canViewSelfReflection = Boolean(onViewSelfReflection && selfReflectionText)
      const canViewAnswers = Boolean(onViewAnswers && hasAssessmentAnswers(original)) || canViewSelfReflection
      const canGoToChart = Boolean(onGoToChart && chartKey && outcome)
      // Nothing has been submitted, so there are no answers to read and no chart point to land on.
      // Both actions still show, and each opens the zero state rather than staying hidden.
      const shouldOpenZeroState = Boolean(onViewPendingAssessment && isPending)
      if (!shouldOpenZeroState && !canViewAnswers && !canGoToChart) return null

      const actionAssessmentLabel = getAssessmentAccessibilityLabel(original, intl)

      const items: HoverActionItem[] = []

      if (shouldOpenZeroState || canGoToChart) {
        items.push({
          id: 'go-to-chart',
          icon: <ProgressIcon size={24} fillColor={colors.iconDefault} />,
          label: formatMessage(MESSAGES.goToChart),
          accessibilityLabel: formatMessage(MESSAGES.goToChartAccessibilityLabel, {
            assessment: actionAssessmentLabel,
          }),
          testId: tID(`CurrentMeasuresTable-${testId}-goToChart`),
          onPress: shouldOpenZeroState ? () => onViewPendingAssessment?.() : () => onGoToChart?.(chartKey as string),
        })
      }

      if (shouldOpenZeroState || canViewAnswers) {
        items.push({
          id: 'view-answers',
          icon: <AssessmentIcon size={24} color={colors.iconDefault} />,
          label: formatMessage(canViewSelfReflection ? MESSAGES.showFullEntry : MESSAGES.viewAnswers),
          accessibilityLabel: formatMessage(
            canViewSelfReflection ? MESSAGES.showFullEntryAccessibilityLabel : MESSAGES.viewAnswersAccessibilityLabel,
            { assessment: actionAssessmentLabel },
          ),
          testId: tID(`CurrentMeasuresTable-${testId}-viewAnswers`),
          onPress: () => openRowDetails(original),
        })
      }

      return (
        <CurrentMeasureHoverActions
          items={items}
          isVisible={isHovered || focusedActionRowId === row.id}
          onFocus={() => setFocusedActionRowId(row.id)}
          onHover={() => setHoveredRowId(row.id)}
          onBlur={() => setFocusedActionRowId(null)}
        />
      )
    }
  }, [
    colors,
    focusedActionRowId,
    formatMessage,
    intl,
    onGoToChart,
    onViewAnswers,
    onViewPendingAssessment,
    onViewSelfReflection,
    openRowDetails,
  ])

  const columns = useMemo(() => {
    const header = (text: string, testID: string, fitContent = false) => (
      <TruncatedText header fitContent={fitContent} resizeKey={tableWidth} testID={testID} text={text} />
    )

    return [
      columnHelper.accessor('measureDisplayName', {
        // Column id drives both the DataTable cell test id and the row header column, so keep it
        // as 'name' rather than letting it follow the row field.
        id: 'name',
        header: () => (
          <HeaderCellContainer>
            {header(formatMessage(MESSAGES.coreAssessmentsHeader), 'CurrentMeasuresTable-coreAssessmentsHeader', true)}
            <Tooltip
              testID={tID('CurrentMeasuresTable-coreAssessments-info')}
              accessibilityLabel={formatMessage(MESSAGES.aboutCoreAssessments)}
              hoverEnabled
              openOnPress
              placement='top'
              content={formatMessage(MESSAGES.coreAssessmentsTooltip)}
              wrapperStyle={{ flexGrow: 0, flexShrink: 0 }}
            >
              <InfoIcon size={16} fillColor={colors.iconDefault} testID={tID('CurrentMeasuresTable-infoIcon')} />
            </Tooltip>
          </HeaderCellContainer>
        ),
        ...getColumnLayout('assessment', 1),
        cell: ({ row }) => (
          <TruncatedText
            accessibilityLabel={getAssessmentAccessibilityLabel(row.original, intl)}
            resizeKey={tableWidth}
            testID={`CurrentMeasuresTable-${row.original.testId}-name`}
            text={row.original.measureDisplayName}
          />
        ),
      }),
      columnHelper.accessor('score', {
        header: () => header(formatMessage(MESSAGES.scoreHeader), 'CurrentMeasuresTable-scoreHeader'),
        ...getColumnLayout('score'),
        cell: ({ row }) => (
          <TruncatedText
            accessibilityLabel={
              row.original.score === EMPTY_VALUE ? formatMessage(MESSAGES.notAvailable) : row.original.score
            }
            disableTooltip={row.original.score === EMPTY_VALUE}
            resizeKey={tableWidth}
            testID={`CurrentMeasuresTable-${row.original.testId}-score`}
            text={row.original.score}
          />
        ),
      }),
      columnHelper.accessor('description', {
        header: () => header(formatMessage(MESSAGES.descriptionHeader), 'CurrentMeasuresTable-descriptionHeader'),
        ...getColumnLayout('description'),
        cell: ({ row }) => {
          const { description, supplementalTooltip, testId } = row.original
          const accessibleDescription = description === EMPTY_VALUE ? formatMessage(MESSAGES.notAvailable) : description
          const assessmentAccessibilityLabel = getAssessmentAccessibilityLabel(row.original, intl)

          const descriptionContent = (
            <InlineCellContainer>
              <TruncatedText
                accessibilityLabel={accessibleDescription}
                disableTooltip={Boolean(supplementalTooltip) || description === EMPTY_VALUE}
                fitContent
                resizeKey={tableWidth}
                testID={`CurrentMeasuresTable-${testId}-description`}
                text={description}
              />
              {supplementalTooltip ? (
                <AdditionalDetailsIconContainer
                  accessible={false}
                  aria-hidden
                  importantForAccessibility='no-hide-descendants'
                >
                  {supplementalTooltip.hasCriticalDetails ? (
                    <AlertLoudIcon
                      fillColor={colors.iconWarning}
                      testID={tID(`CurrentMeasuresTable-${testId}-criticalAlert`)}
                    />
                  ) : (
                    <InfoIcon
                      size={16}
                      fillColor={colors.iconDefault}
                      testID={tID(`CurrentMeasuresTable-${testId}-additionalInfo`)}
                    />
                  )}
                </AdditionalDetailsIconContainer>
              ) : null}
            </InlineCellContainer>
          )

          if (!supplementalTooltip) return descriptionContent

          return (
            <Tooltip
              testID={`CurrentMeasuresTable-${testId}-description-details`}
              content={
                <AdditionalDetailsTooltip
                  rowId={testId}
                  description={description === EMPTY_VALUE ? undefined : description}
                  details={supplementalTooltip.details}
                />
              }
              accessibilityLabel={formatMessage(
                supplementalTooltip.hasCriticalDetails
                  ? MESSAGES.criticalAnswerTrigger
                  : MESSAGES.additionalDetailsTrigger,
                {
                  description: accessibleDescription,
                  assessment: assessmentAccessibilityLabel,
                },
              )}
              hoverEnabled
              openOnPress
              placement='top'
              tooltipSize={TooltipMaxWidth.WIDE}
              wrapperStyle={{ flexGrow: 1, flexShrink: 1, maxWidth: '100%', minWidth: 0, display: 'flex' }}
              style={
                {
                  flexGrow: 1,
                  flexShrink: 1,
                  maxWidth: '100%',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                } as ViewStyle
              }
            >
              {descriptionContent}
            </Tooltip>
          )
        },
      }),
      columnHelper.display({
        id: 'frequency',
        header: () => (
          <HeaderCellContainer>
            {header(formatMessage(MESSAGES.frequencyHeader), 'CurrentMeasuresTable-frequencyHeader', true)}
            <Tooltip
              testID={tID('CurrentMeasuresTable-frequency-info')}
              accessibilityLabel={formatMessage(MESSAGES.aboutFrequency)}
              hoverEnabled
              openOnPress
              placement='top'
              content={formatMessage(MESSAGES.frequencyTooltip)}
              wrapperStyle={{ flexGrow: 0, flexShrink: 0 }}
            >
              <InfoIcon
                size={16}
                fillColor={colors.iconDefault}
                testID={tID('CurrentMeasuresTable-frequency-infoIcon')}
              />
            </Tooltip>
          </HeaderCellContainer>
        ),
        ...getColumnLayout('frequency'),
        cell: ({ row }) => {
          const { isScreenerOnly, selfReflectionText, testId } = row.original

          if (!isScreenerOnly) {
            // Every automated measure still in rotation goes out with the pre-session assessment,
            // so the frequency is fixed. The member self reflection is not a scored measure on a
            // schedule of its own, so its cell stays empty.
            const isSelfReflection = Boolean(selfReflectionText)
            return (
              <TruncatedText
                accessibilityLabel={isSelfReflection ? formatMessage(MESSAGES.notAvailable) : undefined}
                disableTooltip={isSelfReflection}
                resizeKey={tableWidth}
                testID={`CurrentMeasuresTable-${testId}-frequency`}
                text={isSelfReflection ? EMPTY_VALUE : formatMessage(MESSAGES.everySession)}
              />
            )
          }

          const assessmentAccessibilityLabel = getAssessmentAccessibilityLabel(row.original, intl)

          return (
            <InlineCellContainer>
              <TruncatedText
                accessibilityLabel={formatMessage(MESSAGES.screenerOnly)}
                color={colors.textSecondary}
                disableTooltip
                fitContent
                resizeKey={tableWidth}
                testID={`CurrentMeasuresTable-${testId}-frequency`}
                text={formatMessage(MESSAGES.screenerOnly)}
              />
              <Tooltip
                testID={tID(`CurrentMeasuresTable-${testId}-screenerOnly-info`)}
                accessibilityLabel={formatMessage(MESSAGES.aboutScreenerOnly, {
                  assessment: assessmentAccessibilityLabel,
                })}
                hoverEnabled
                openOnPress
                placement='top'
                content={formatMessage(MESSAGES.screenerOnlyTooltip)}
                wrapperStyle={{ flexGrow: 0, flexShrink: 0 }}
              >
                <InfoIcon
                  size={16}
                  fillColor={colors.iconDefault}
                  testID={tID(`CurrentMeasuresTable-${testId}-screenerOnly-infoIcon`)}
                />
              </Tooltip>
            </InlineCellContainer>
          )
        },
      }),
      columnHelper.accessor('lastTakenDate', {
        header: () => header(formatMessage(MESSAGES.lastTakenHeader), 'CurrentMeasuresTable-lastTakenHeader'),
        ...getColumnLayout('lastTakenDate'),
        cell: ({ row }) => {
          const { hasDuplicateAttempts, lastTakenDate, testId } = row.original
          const parsedDate = parseOutcomeResponseDate(lastTakenDate)
          if (!parsedDate) {
            return (
              <TruncatedText
                accessibilityLabel={formatMessage(MESSAGES.notAvailable)}
                disableTooltip
                resizeKey={tableWidth}
                testID={`CurrentMeasuresTable-${testId}-lastTakenDate`}
                text={EMPTY_VALUE}
              />
            )
          }

          const submittedAt = hasDuplicateAttempts ? getAssessmentSubmissionDateTime(lastTakenDate, intl) : undefined

          return (
            <TruncatedText
              accessibilityLabel={
                submittedAt ?? formatDate(parsedDate, { month: 'long', day: 'numeric', year: 'numeric' })
              }
              resizeKey={tableWidth}
              testID={`CurrentMeasuresTable-${testId}-lastTakenDate`}
              text={formatDate(parsedDate, { month: 'numeric', day: 'numeric', year: 'numeric' })}
              tooltipContent={submittedAt}
            />
          )
        },
      }),
    ]
  }, [colors, formatDate, formatMessage, intl, tableWidth])

  return (
    <CardContainer
      testID={tID('CurrentMeasuresTable')}
      onLayout={({ nativeEvent }) => setTableWidth(nativeEvent.layout.width)}
    >
      <CardHeaderContainer>
        <Title level='3' size={TitleSize.XSMALL} text={tableTitle} />
        <SelectDateDropdown
          testID={tID('CurrentMeasuresTable-selectDate')}
          triggerTestID={tID('CurrentMeasuresTable-selectDate-trigger')}
          dropdownTestID={tID('CurrentMeasuresTable-selectDate-dropdown')}
          accessibilityLabel={formatMessage(MESSAGES.selectDateAccessibilityLabel, {
            selection: selectedDateOption?.accessibilityLabel ?? formatMessage(MESSAGES.lastKnownMeasures),
          })}
          buttonSize={ButtonSize.UNSIZED}
          buttonType={ButtonType.SECONDARY}
          contentContainerStyle={{ gap: 4 }}
          customButtonText={
            hasSelectedDate
              ? (selectedDateOption?.triggerText ?? selectedDateOption?.text)
              : formatMessage(MESSAGES.selectDate)
          }
          customOptionsConfig={dateOptionsConfig}
          customTextColor={colors.textActive}
          customTextSize={BodyTextSize.BADGE}
          customTextStyle={{ lineHeight: 16 }}
          dropdownAccessibilityLabel={formatMessage(MESSAGES.dateOptionsAccessibilityLabel)}
          dropdownItems={dateOptions}
          dropdownStyle={{ borderRadius: 16, paddingVertical: 8, right: 0 }}
          dropdownWidth={280}
          hideChevron
          iconColor={colors.textActive}
          leftIconNoMargin
          leftIcon={
            <CalendarEmptyIcon width={16} height={16} fillColor={colors.textActive} strokeColor={colors.textActive} />
          }
          value={selectedDateOption?.id ?? LAST_KNOWN_MEASURES}
        />
      </CardHeaderContainer>
      {shouldShowCoreAssessments && (
        <View testID={tID('CurrentMeasuresTable-coreAssessments')}>
          <DataTable
            accessibilityLabel={formatMessage(MESSAGES.currentMeasuresTable, { title: tableTitle })}
            columns={columns}
            data={isLoading ? undefined : rows}
            getRowId={getMeasureRowId}
            cellAlignment='left'
            emptyTableComponent={<View />}
            emptyTableText=''
            rowHeaderColumn='name'
            striped
            rowHeight={42}
            hoveredRowId={hoveredRowId}
            onRowHover={renderRowActions || canOpenRowDetails ? setHoveredRowId : undefined}
            onRowClick={canOpenRowDetails ? handleRowPress : undefined}
            rowActions={renderRowActions}
            tableContainerStyle={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, paddingBottom: 4 }}
            withTableSemantics
          />
        </View>
      )}
      {renderAddedAssessments?.({ showSharedColumnHeaders: !shouldShowCoreAssessments })}
      {onAddAssessment && (
        <AddAssessmentRow testID={tID('CurrentMeasuresTable-addAssessmentRow')}>
          <Pressable
            accessibilityRole='button'
            accessibilityLabel={formatMessage(MESSAGES.addAssessmentAccessibilityLabel)}
            testID={tID('ClientProgress-addAssessment')}
            onPress={onAddAssessment}
          >
            {({ hovered }: PressableStateCallbackType) => (
              <BodyText
                color={hovered ? colors.linkHover : colors.linkDefault}
                size={BodyTextSize.SMALL}
                text={formatMessage(MESSAGES.addAssessment)}
                underline={hovered}
              />
            )}
          </Pressable>
        </AddAssessmentRow>
      )}
    </CardContainer>
  )
}
